import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '../../assets/icons/close.svg?react';
import RemoveColIcon from '../../assets/icons/remove-col.svg?react';
import ReorderIcon from '../../assets/icons/reorder.svg?react';
import SearchIcon from '../../assets/icons/search.svg?react';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';

interface SortableColumnItemProps {
  column: ColumnConfig;
  onRemove: (columnId: string) => void;
}

function SortableColumnItem({ column, onRemove }: SortableColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-border-secondary bg-surface-primary isolate flex h-13
        w-full shrink-0 cursor-grab items-center gap-6 overflow-hidden
        rounded-lg border px-4 py-4 active:cursor-grabbing`}
      {...attributes}
      {...listeners}
    >
      <ReorderIcon className='text-secondary h-5 w-5 shrink-0' />
      <p
        className='size-p2 text-secondary min-h-0 min-w-0 flex-1 leading-[1.3]
          font-bold'
      >
        {column.label}
      </p>
      {column.canHide ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(column.id);
          }}
          className='h-5 w-5 shrink-0 overflow-clip'
          aria-label={`Remove ${column.label}`}
        >
          <RemoveColIcon className='text-secondary h-5 w-5 cursor-pointer' />
        </button>
      ) : (
        <div className='h-5 w-5 shrink-0' />
      )}
    </div>
  );
}

export interface ColumnConfig {
  id: string;
  label: string;
  isVisible: boolean;
  canHide: boolean; // Some columns like 'select' and 'actions' shouldn't be hideable
}

export interface ManageColumnsDialogProps<TData> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDef<TData>[];
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  /** Default column visibility for "Reset to default" functionality */
  defaultColumnVisibility?: VisibilityState;
  onColumnsChange: (visibleColumnIds: string[], columnOrder: string[]) => void;
  /** Called when "Reset to default" is clicked to reset column sizes */
  onResetColumnSizing?: () => void;
}

export function ManageColumnsDialog<TData>({
  open,
  onOpenChange,
  columns,
  columnVisibility = {},
  columnOrder = [],
  defaultColumnVisibility,
  onColumnsChange,
  onResetColumnSizing,
}: ManageColumnsDialogProps<TData>) {
  const [searchQuery, setSearchQuery] = useState('');

  // Build default column configs (respecting defaultColumnVisibility if provided)
  const defaultColumnConfigs = useMemo(() => {
    return columns
      .filter((col) => {
        const id =
          'id' in col
            ? col.id
            : 'accessorKey' in col
              ? String(col.accessorKey)
              : '';
        return id !== 'select' && id !== 'actions';
      })
      .map((col) => {
        const id: string =
          'id' in col && col.id
            ? col.id
            : 'accessorKey' in col
              ? String(col.accessorKey)
              : '';
        // Use defaultColumnVisibility if provided, otherwise default to true
        const isVisible =
          defaultColumnVisibility && id && id in defaultColumnVisibility
            ? defaultColumnVisibility[id] !== false
            : true;
        return {
          id: id || '',
          label:
            typeof col.header === 'string'
              ? col.header
              : id
                ? id.charAt(0).toUpperCase() +
                  id.slice(1).replace(/([A-Z])/g, ' $1')
                : '',
          isVisible,
          canHide: col.enableHiding !== false,
        };
      })
      .filter((col) => col.id);
  }, [columns, defaultColumnVisibility]);

  // Build column configs from column definitions (respecting saved visibility/order)
  const initialColumnConfigs = useMemo(() => {
    const configs = columns
      .filter((col) => {
        const id =
          'id' in col
            ? col.id
            : 'accessorKey' in col
              ? String(col.accessorKey)
              : '';
        return id !== 'select' && id !== 'actions';
      })
      .map((col) => {
        const id =
          'id' in col
            ? col.id
            : 'accessorKey' in col
              ? String(col.accessorKey)
              : '';
        // Check visibility - default to true if not in visibility state
        // (empty visibility state means all columns visible)
        const isVisible =
          id && id in columnVisibility ? columnVisibility[id] !== false : true;
        return {
          id: id || '',
          label:
            typeof col.header === 'string'
              ? col.header
              : id
                ? id.charAt(0).toUpperCase() +
                  id.slice(1).replace(/([A-Z])/g, ' $1')
                : '',
          isVisible: isVisible === true, // Ensure boolean
          canHide: col.enableHiding !== false,
        };
      })
      .filter((col) => col.id); // Filter out any with empty IDs

    // If there's a column order, sort configs by it
    if (columnOrder.length > 0) {
      configs.sort((a, b) => {
        const aIndex = columnOrder.indexOf(a.id);
        const bIndex = columnOrder.indexOf(b.id);
        // If both are in order, sort by their order position
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        // If only one is in order, it comes first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        // Otherwise maintain original order
        return 0;
      });
    }

    return configs;
  }, [columns, columnVisibility, columnOrder]);

  const [columnConfigs, setColumnConfigs] =
    useState<ColumnConfig[]>(initialColumnConfigs);
  const [orderedColumns, setOrderedColumns] = useState<ColumnConfig[]>(() =>
    initialColumnConfigs.filter((c) => c.isVisible)
  );

  // Sync internal state only when the dialog opens. The parent may re-render
  // while the dialog is open (table refit, viewport change); syncing on every
  // such render wiped the search text and unsaved ticks.
  const initialColumnConfigsRef = useRef(initialColumnConfigs);
  useEffect(() => {
    initialColumnConfigsRef.current = initialColumnConfigs;
  }, [initialColumnConfigs]);
  useEffect(() => {
    if (!open) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const configs = initialColumnConfigsRef.current;
      setColumnConfigs(configs);
      setOrderedColumns(configs.filter((c) => c.isVisible));
      setSearchQuery('');
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [open]);

  // Filter columns based on search query and sort: non-hideable first, then alphabetically
  const filteredColumns = useMemo(() => {
    const filtered = !searchQuery.trim()
      ? columnConfigs
      : columnConfigs.filter((col) =>
          col.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
    return [...filtered].sort((a, b) => {
      // Non-hideable columns always come first
      if (!a.canHide && b.canHide) return -1;
      if (a.canHide && !b.canHide) return 1;
      // Then sort alphabetically
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    });
  }, [columnConfigs, searchQuery]);

  // Toggle column visibility
  const handleToggleColumn = useCallback(
    (columnId: string) => {
      const column = columnConfigs.find((c) => c.id === columnId);
      if (!column || !column.canHide) return;

      setColumnConfigs((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, isVisible: !col.isVisible } : col
        )
      );
      setOrderedColumns((prev) => {
        if (!column) return prev;

        if (column.isVisible) {
          // Removing from ordered list
          return prev.filter((c) => c.id !== columnId);
        } else {
          // Adding to ordered list
          return [...prev, { ...column, isVisible: true }];
        }
      });
    },
    [columnConfigs]
  );

  // Remove column from ordered list (same as unchecking)
  const handleRemoveFromOrder = useCallback(
    (columnId: string) => {
      const column = columnConfigs.find((c) => c.id === columnId);
      if (!column || !column.canHide) return;

      setColumnConfigs((prev) =>
        prev.map((col) =>
          col.id === columnId ? { ...col, isVisible: false } : col
        )
      );
      setOrderedColumns((prev) => prev.filter((c) => c.id !== columnId));
    },
    [columnConfigs]
  );

  // Handle drag and drop reordering with @dnd-kit
  const [activeId, setActiveId] = useState<string | null>(null);
  // The drag overlay renders in a body portal, so it needs the list width
  const orderListRef = useRef<HTMLDivElement>(null);
  const [overlayWidth, setOverlayWidth] = useState<number>();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setOverlayWidth(orderListRef.current?.clientWidth);
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedColumns((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    setActiveId(null);
  };

  // Save changes
  const handleSave = () => {
    const visibleColumnIds = orderedColumns.map((c) => c.id);
    const columnOrder = ['select', ...visibleColumnIds, 'actions'];
    onColumnsChange(visibleColumnIds, columnOrder);
    onOpenChange(false);
  };

  // Reset to default - applies changes immediately
  const handleResetToDefault = () => {
    const visibleDefaults = defaultColumnConfigs.filter((c) => c.isVisible);
    const visibleColumnIds = visibleDefaults.map((c) => c.id);
    const newColumnOrder = ['select', ...visibleColumnIds, 'actions'];

    // Update local state
    setColumnConfigs(defaultColumnConfigs);
    setOrderedColumns(visibleDefaults);

    // Apply changes immediately
    onColumnsChange(visibleColumnIds, newColumnOrder);

    // Also reset column sizes if callback provided
    onResetColumnSizing?.();
  };

  // Cancel
  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className='fixed inset-0 z-70 bg-black/50' />
        <Dialog.Content
          className='bg-surface-primary fixed top-1/2 left-1/2 z-70 flex
            h-[42rem] max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-200
            -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden
            rounded-xl shadow-[0px_12px_24px_0px_rgba(0,0,0,0.1)]'
          aria-describedby='manage-columns-description'
        >
          {/* Header */}
          <div className='flex shrink-0 items-start justify-between gap-4 p-6'>
            <div className='flex flex-col gap-1'>
              <Dialog.Title className='size-h4 text-primary font-bold'>
                Manage columns
              </Dialog.Title>
              <Dialog.Description
                id='manage-columns-description'
                className='size-caption text-primary leading-[1.2]'
              >
                Select the columns you want to see and what order they appear in
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                className='text-secondary hover:text-primary shrink-0
                  cursor-pointer rounded-sm transition-colors'
                aria-label='Close'
              >
                <CloseIcon className='h-6 w-6' />
              </button>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div
            className='border-border-secondary flex min-h-0 w-full flex-1
              flex-col border-y md:flex-row'
          >
            {/* Left Panel - Column Options */}
            <div
              className='border-border-secondary flex min-h-0 min-w-0 flex-1
                flex-col gap-4 border-b pt-6 pr-0 pb-0 pl-6 md:w-1/2 md:border-r
                md:border-b-0'
            >
              <div className='flex w-full flex-col gap-4 pr-6'>
                <h3
                  className='size-p1 text-primary w-full leading-[1.3]
                    font-bold'
                >
                  Columns options
                </h3>
                {/* Search */}
                <div className='relative'>
                  <span
                    className='text-disabled pointer-events-none absolute
                      top-1/2 left-2 flex h-4 w-4 -translate-y-1/2 items-center
                      justify-center'
                  >
                    <SearchIcon />
                  </span>
                  <Input
                    type='text'
                    placeholder='Search name, metrics'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className='pl-8'
                  />
                </div>
              </div>

              {/* Column List */}
              <div
                className='flex w-full flex-1 flex-col gap-3 overflow-y-auto
                  pr-6 pb-6'
              >
                {filteredColumns.map((column) => (
                  <label
                    key={column.id}
                    className={`hover:bg-surface-secondary flex w-full
                    cursor-pointer items-center gap-3 rounded-lg p-2
                    ${!column.canHide ? 'cursor-default' : ''}`}
                  >
                    <Checkbox
                      checked={column.isVisible}
                      onCheckedChange={() => handleToggleColumn(column.id)}
                      disabled={!column.canHide}
                    />
                    <span
                      className={`size-p2 min-h-0 min-w-0 flex-1 leading-[1.3]
                      ${column.isVisible ? 'text-primary' : 'text-secondary'}`}
                    >
                      {column.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Right Panel - Reorder Columns */}
            <div
              className='flex min-h-0 min-w-0 flex-1 flex-col gap-4 pt-6 pr-0
                pb-0 pl-6 md:w-1/2'
            >
              <h3
                className='size-p1 text-primary w-full pr-6 leading-[1.3]
                  font-bold'
              >
                Reorder columns
              </h3>

              {/* Ordered Column List */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              >
                <SortableContext
                  items={orderedColumns.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div
                    ref={orderListRef}
                    className='flex w-full flex-1 flex-col items-end gap-3
                      overflow-y-auto pr-6 pb-6'
                  >
                    {orderedColumns.map((column) => (
                      <SortableColumnItem
                        key={column.id}
                        column={column}
                        onRemove={handleRemoveFromOrder}
                      />
                    ))}
                  </div>
                </SortableContext>
                {createPortal(
                  <DragOverlay>
                    {activeId ? (
                      <div
                        style={{ width: overlayWidth }}
                        className='border-border-primary
                          bg-surface-primary-support isolate flex h-13
                          cursor-grabbing items-center gap-6 overflow-hidden
                          rounded-lg border px-4 py-4
                          shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1)]'
                      >
                        <ReorderIcon className='text-secondary h-5 w-5 shrink-0' />
                        <p
                          className='size-p2 text-secondary min-h-0 min-w-0
                            flex-1 leading-[1.3] font-bold'
                        >
                          {orderedColumns.find((c) => c.id === activeId)?.label}
                        </p>
                        <div className='h-5 w-5 shrink-0' />
                      </div>
                    ) : null}
                  </DragOverlay>,
                  document.body
                )}
              </DndContext>
            </div>
          </div>

          {/* Footer */}
          <div
            className='flex shrink-0 items-center justify-between gap-3 px-6
              py-4'
          >
            <Button variant='tertiary' onClick={handleCancel}>
              Cancel
            </Button>
            <div className='flex items-center gap-3'>
              <Button variant='secondary' onClick={handleResetToDefault}>
                Reset to default
              </Button>
              <Button variant='primary' onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
